import { FC, useCallback } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, Card, CardBody, Flex, Text, VStack } from '@bonusly/design-system';

import { useGithubAuthorize, useGithubDisconnect, useGithubStatus } from '../../queries/github';

interface GithubConnectCardProps {
	returnPath?: string;
}

export const GithubConnectCard: FC<GithubConnectCardProps> = ({ returnPath }) => {
	const { data: status, isLoading: isLoadingStatus } = useGithubStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useGithubAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGithubDisconnect();
	const { track } = useAnalytics();

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Github.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Github.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;

	const connectionStatus = status?.status;
	const isConnected = connectionStatus === 'connected';
	const isExpired = connectionStatus === 'expired';

	const statusLabel = isConnected ? 'Connected' : isExpired ? 'Reconnect required' : 'Not connected';
	const statusColor = isExpired ? 'text.warning' : 'text.softer';

	return (
		<Card variant="outline" borderColor="border.soft" borderRadius="lg">
			<CardBody py={3} px={4}>
				<Flex alignItems="center" justifyContent="space-between">
					<VStack spacing={0} align="flex-start">
						<Text fontWeight="semibold" fontSize="sm" color="text.default">
							GitHub
						</Text>
						<Text fontSize="xs" color={statusColor}>
							{statusLabel}
						</Text>
					</VStack>
					{isConnected ? (
						<Button size="sm" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
							Disconnect
						</Button>
					) : (
						<Button size="sm" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
							{isExpired ? 'Reconnect' : 'Connect'}
						</Button>
					)}
				</Flex>
			</CardBody>
		</Card>
	);
};
